import dynamic from 'next/dynamic'

type APIUserType = {
  UPDATE_USER: (id: any) => string
}

export const CSS_BREAKPOINTS = {
  small: '599px',
  medium: '959px',
  large: '990px',
  xlarge: '1200px',
}

export const CSS_BREAKPOINTS_V2 = {
  mobileXL: '400px',
  tablet: '768px',
  desktopS: '1024px',
  desktopM: '1280px',
  desktopL: '1440px',
  desktopXL: '1792px',
  desktopXXL: '1920px',
}

export const CHAT_ROOM_FILTER_KEYS = {
  COLLABORATION: 'collaboration',
  BROCHURE: 'brochure',
  ENQUIRY: 'enquiry',
  ARCHIVED: 'archived',
  ALL: 'all',
} as const

export const CUSTOMER_MESSAGE_TYPES_FILTER_KEYS = {
  ARCHIVED: 'archived',
} as const

export const CHAT_ROOM_SORT_KEYS = {
  DESC: 'desc',
  ASC: 'asc',
  UNREAD: 'unread',
  RESPONDED: 'responded',
} as const

type Keys<T> = keyof T
type ValuesType<T> = T[Keys<T>]

type ChatRoomQueryType = {
  page: number
  limit: number
  keyword?: string
  messageType?: ValuesType<typeof CHAT_ROOM_FILTER_KEYS>
  businessCategoryId?: any
  sortKey: ValuesType<typeof CHAT_ROOM_SORT_KEYS>
}

type ImageLibraryType = {
  ALL: string
  BY_ID: (id: number) => string
}

export const optionsSortBy = [
  {
    label: 'Oldest - newest',
    value: CHAT_ROOM_SORT_KEYS.ASC,
  },
  {
    label: 'Newest - oldest',
    value: CHAT_ROOM_SORT_KEYS.DESC,
  },
  {
    label: 'Unread',
    value: CHAT_ROOM_SORT_KEYS.UNREAD,
  },
]

export const optionFilterBy = [
  {
    label: 'All',
    value: CHAT_ROOM_FILTER_KEYS.ALL,
  },
  {
    label: 'Brochure request',
    value: CHAT_ROOM_FILTER_KEYS.BROCHURE,
  },
  {
    label: 'Collaboration request',
    value: CHAT_ROOM_FILTER_KEYS.COLLABORATION,
  },
  {
    label: 'Archived',
    value: CHAT_ROOM_FILTER_KEYS.ARCHIVED,
  },
  {
    label: 'Enquiry',
    value: CHAT_ROOM_FILTER_KEYS.ENQUIRY,
  },
]

export const customerMessageTypeOptionFilterBy = [
  {
    label: 'Archived',
    value: CUSTOMER_MESSAGE_TYPES_FILTER_KEYS.ARCHIVED,
  },
]

export const CONSOLE_DEEPER = {
  SUPPLER_LISTINGS: 'supplier-listings',
  SUPPLER_TAGS: 'supplier-tags',
  EDITOR_CONTENT: 'content',
  SUPPLIER_ACCOUNT: 'supplier-account',
  COMPOSER: 'composer',
}

type ADMIN_API = {
  GET_SUPPLIER_LISTINGS: (
    page: number,
    query: string,
    status: string[] | null,
    business_category_id: string[] | null
  ) => string
  GET_SUPPLIER_ID: (id: string) => string
  EXPORT_CUSTOMER: string
  GET_FEATURE_SERVICE: (page: number, filter: string) => string
  GET_FEATURE_SERVICE_BY_TAG: (page: number, tag: string) => string
  CREATE_NEW_TAG: string
  CREATE_SUPPLIER_TAG: string
  DELETE_SUPPLIER_TAG: (id: number) => string
  GET_MEMBER_LIST: (page: number, search: string) => string
  UPDATE_SUPPLIER_ACCOUNT_BY_ID: (id: number) => string
  GET_SUPPLIER_MANAGER_LIST: string
  GET_LISTING_BY_ACCOUNT: (id: number, page: number) => string
  SUPPLIER_NUDGE_APPLY: (id: number) => string
  DELETE_MEMBER: (id: number) => string
  UPDATE_MEMBER_BY_ID: (id: number) => string
  EXPORT_SUPPLIER_ACCOUNT: (type: string) => string
  GET_MEMBER_EXPORT: string
  GET_TAG_BY_SECTION: (section: string) => string
  CREATE_TAG_ARTICLE: string
  SUPPLIER_NUDGE_LISTING: (id: number) => string
}

type APIType = {
  GET_SIGN_UP_LIST: string
  GET_LOCATION_LIST: string
  GET_REGION_LIST: string
  GET_GUEST_LIST: string
  GET_BUDGET_LIST: string
  GET_ONBOARDING_FORM_TWO: string
  GET_MONTHLY_PRICE_FIRST_PAYMENT_DATE: string
  CUSTOMER_EVENT: string
  USERS: APIUserType
  GET_TAG: String
  GET_TAG_BY_CATEGORY_AND_SECTION: (category: string, section: string) => string
  GET_TAG_BY_CATEGORY: (category: string) => string
  DELETE_COLLABORATOR: (id: number, supplierID: number) => string
  GET_BLOCK_TYPE: string
  CREATE_PAYMENT_INTENT: string
  UPDATE_SUPPLIER_LISTING: (id: string) => String
  PUT_SUPPLIER: (id: string) => string
  REQUEST_COLLABORATOR: (id: string) => string
  GET_SUPPLIER: string
  SUBMIT_FOR_APPROVAL: (id: number) => string
  GET_CHAT_ROOM: ({
    businessCategoryId,
    page,
    limit,
    keyword,
    messageType,
    sortKey,
  }: ChatRoomQueryType) => string
  SEARCH_CHAT_ROOM: string
  MARK_MESSAGE: string
  COLLABORATION_RESPONSE: string
  GET_CONVERSTATION: (id: number) => string
  CREATE_CONVERSTATION: (id: number) => string
  UPDATE_CHATROOM_STATUS: (id: number) => string
  GET_CHATROOM_CHAT: string
  ARCHIVE_CHAT: string
  ADD_NEW_TASK: string
  GET_CHECKLIST_TASKS: string
  GET_ARTICLES: string
  UPDATE_CHECKLIST_TASKS: (timeBefore: number, id: number) => string
  DELETE_CHECKLIST_TASKS: (timeBefore: number, id: number) => string
  GET_SUPPLIER_BY_ID: (id: number) => string
  ADMIN: ADMIN_API
  GET_IMAGE_LIBRARY: ImageLibraryType
  ARTICLE: {
    ADMIN_GET: (params: string) => string
    GET: (params: string) => string
    GET_BY_ID: (id: string) => string
    POST: string
    PUT: (id: string) => string
  }
}

type SupplierType = {
  LISTING: (form: string) => string
  DASHBOARD: string
}

type OnboardingType = {
  BOARD: (board: string) => string
}

type ConsoleType = {
  EDITOR: (deepUrl: string) => string
  CUSTOMER: string
  MEMBER: string
  SUPPLIERS: (deepUrl: string) => string
  MEDIA_LIBRARY: string
}

type AdminType = {
  HOME: string
  CONSOLE: ConsoleType
}

type DomainType = {
  CREATE: string
  EDIT: string
  NOT_FOUND: string
  AUTH: string
  ADMIN_PORTAL: AdminType
  ONBOARDING: OnboardingType
  HOME: string
  SUPPLIER: SupplierType
  API: APIType
  SUPPLIER_THANK_YOU: string
  CONGRATULATIONS: string
  SUPPLIER_SUMMARY: string
  SUPPLIER_MESSAGES: string
  SUPPLIER_ACCOUNT: string
  SUPPLIER_LISTINGS: string
  SUPPLIER_BUS_HUB: string
  SUPPLIER_SUB_REAL_WED: string
  CUSTOMER_DASHBOARD: (page: string) => string
  SEARCH: string
  VENUE: string
  LANDING_PAGE: string
  SEND_RESET_PASSWORD: string
  EXPLORE_ALL_PODCAST: string
}

export const DOMAIN_ACTION: DomainType = {
  CREATE: 'create',
  EDIT: 'edit',
  NOT_FOUND: '404',
  SEND_RESET_PASSWORD: '/forgetten-password',
  AUTH: '/auth',
  ADMIN_PORTAL: {
    HOME: '/admin',
    CONSOLE: {
      EDITOR: (deepUrl: string = '') => `/admin/editor-console${`/${deepUrl}`}`,
      CUSTOMER: '/admin/customer-console',
      MEMBER: '/admin/member-console',
      SUPPLIERS: (deepUrl: string = '') =>
        `/admin/supplier-console${`/${deepUrl}`}`,
      MEDIA_LIBRARY: '/admin/media-library',
    },
  },
  SUPPLIER_THANK_YOU: '/supplier/thankyou',
  SUPPLIER_MESSAGES: '/supplier/dashboard/messages',
  SUPPLIER_SUMMARY: '/supplier/dashboard/summary',
  SUPPLIER_ACCOUNT: '/supplier/dashboard/account?tab=account',
  SUPPLIER_LISTINGS: '/supplier/dashboard/listings',
  SUPPLIER_BUS_HUB: '/supplier/dashboard/business-hub',
  SUPPLIER_SUB_REAL_WED: '/supplier/dashboard/submit-real-wedding',
  HOME: '/',
  CONGRATULATIONS: '/supplier/congrats',
  SUPPLIER: {
    LISTING: (form: string) => `/supplier/listing?formName=${form}`,
    DASHBOARD: '/supplier/dashboard',
  },
  ONBOARDING: {
    BOARD: (board: string) => `/onboarding?board=${board}`,
  },
  CUSTOMER_DASHBOARD: (page: string) => `/customer/dashboard/${page}`,
  SEARCH: '/search',
  LANDING_PAGE: '/',
  VENUE: 'venue',
  API: {
    GET_REGION_LIST: '/api/regions',
    GET_SIGN_UP_LIST: '/api/getSignupList',
    GET_LOCATION_LIST: '/api/getLocations',
    GET_GUEST_LIST: '/customer-events/number-of-guests',
    GET_BUDGET_LIST: '/customer-events/ideal-budget',
    GET_ONBOARDING_FORM_TWO: '/api/getOnbaordingFormTwo',
    GET_BLOCK_TYPE: ' /api/getBlockType',
    REQUEST_COLLABORATOR: (id: string) => `/api/requestCollaborator/${id}`,
    USERS: {
      UPDATE_USER: (id) => `/api/user/${id}`,
    },
    PUT_SUPPLIER: (id: string) => `/api/supplier/${id}`, // Endpoint for upload supplier
    GET_SUPPLIER_BY_ID: (id: number) => `/api/supplier/get/${id}`, // Endpoint for upload supplier,
    UPDATE_SUPPLIER_LISTING: (id: string = '') =>
      `/api/updateSupplierAccountDetails/${id}`,
    CUSTOMER_EVENT: '/api/putCustomerEvent',
    GET_TAG: '/tags',
    GET_TAG_BY_CATEGORY_AND_SECTION: (category: string, section: string) =>
      `/api/getTags?category=${category}&section=${section}`,
    GET_TAG_BY_CATEGORY: (category: string) =>
      category
        ? `/api/getTags?category=${category}&section=supplier`
        : `/api/getTags`,
    CREATE_PAYMENT_INTENT: '/api/createPaymentIntent',
    GET_SUPPLIER: '/api/supplier/list',
    GET_MONTHLY_PRICE_FIRST_PAYMENT_DATE: '/api/getMonthlyPriceAndFirstPayment',
    DELETE_COLLABORATOR: (id: number, supplier_id: number) =>
      `/api/deleteCollaborator/${id}/${supplier_id}`,
    MARK_MESSAGE: '/api/markMessage',
    COLLABORATION_RESPONSE: '/api/collaborationResponse',
    ARCHIVE_CHAT: '/api/archiveChat',
    SUBMIT_FOR_APPROVAL: (id: number) => `/api/submitForApproval/${id}`,
    GET_CHAT_ROOM: ({
      businessCategoryId,
      page,
      limit,
      keyword,
      messageType,
      sortKey,
    }) =>
      `/api/getChatRoom?businessCategoryId=${businessCategoryId}&page=${page}&limit=${limit}&keyword=${keyword}&messageType=${messageType}&sortKey=${sortKey}`,
    SEARCH_CHAT_ROOM: '/api/searchChatRoom',
    GET_CHATROOM_CHAT: `/api/getChatroomChat`,
    GET_CONVERSTATION: (id: number) => `/api/message/${id}/conversations`,
    CREATE_CONVERSTATION: (id: number) => `/api/message/${id}/conversation`,
    UPDATE_CHATROOM_STATUS: (id: number) => `/api/message/${id}`,
    ADD_NEW_TASK: '/api/addNewChecklist',
    GET_CHECKLIST_TASKS: '/api/getChecklistTasks',
    GET_ARTICLES: `/articles`,
    UPDATE_CHECKLIST_TASKS: (timeBefore, id) =>
      `/api/updateChecklist?timeBefore=${timeBefore}&id=${id}`,
    DELETE_CHECKLIST_TASKS: (timeBefore, id) =>
      `/api/deleteChecklist?timeBefore=${timeBefore}&id=${id}`,
    ADMIN: {
      GET_SUPPLIER_LISTINGS: (page, query, status, business_category_id) =>
        `/api/admin/supplier/getListings?page=${page}&query=${query}&status=${status}&business_category_id=${business_category_id}`,
      GET_SUPPLIER_ID: (id) => `/api/admin/supplier/${id}`,
      GET_FEATURE_SERVICE: (page, filter) =>
        `/api/admin/tags/getFeatureService?page=${page}&filter=${filter}`,
      GET_FEATURE_SERVICE_BY_TAG: (page, tag) =>
        `/api/admin/tags/getFeatureServiceByTag?page=${page}&tag=${tag}`,
      CREATE_NEW_TAG: '/api/admin/tags/createTag',
      EXPORT_CUSTOMER: `/api/admin/customer/exportCustomer`,
      CREATE_SUPPLIER_TAG: '/api/admin/supplier/createTag',
      DELETE_SUPPLIER_TAG: (id) => `/api/admin/supplier/deleteTag/${id}`,
      GET_MEMBER_LIST: (page, search) =>
        `/api/admin/member/getMemberList?page=${page}&search=${search}`,
      UPDATE_SUPPLIER_ACCOUNT_BY_ID: (id) =>
        `/api/admin/supplier/updateSupplierAccount/${id}`,
      GET_SUPPLIER_MANAGER_LIST: '/api/admin/supplier/getListManager',
      GET_LISTING_BY_ACCOUNT: (id, page) =>
        `/api/admin/supplier/getListingByAccount?page=${page}&id=${id}`,
      SUPPLIER_NUDGE_APPLY: (id) => `/api/admin/supplier/nudge/apply/${id}`,
      DELETE_MEMBER: (id) => `/api/admin/member/deleteMember/${id}`,
      UPDATE_MEMBER_BY_ID: (id) => `/api/admin/member/updateMember/${id}`,
      EXPORT_SUPPLIER_ACCOUNT: (type) =>
        `/api/admin/supplier/exportAccount?type=${type}`,
      GET_MEMBER_EXPORT: `/api/admin/member/export`,
      GET_TAG_BY_SECTION: (section: string) =>
        section
          ? `/api/admin/composer/getTags?section=${section}`
          : `/api/getTags`,
      CREATE_TAG_ARTICLE: 'api/admin/composer/createTag',
      SUPPLIER_NUDGE_LISTING: (id) => `/api/admin/supplier/nudge/listing/${id}`,
    },
    GET_IMAGE_LIBRARY: {
      ALL: '/api/getImageLibrary/all',
      BY_ID: (id: number) => `/api/getImageLibrary/${id}`,
    },
    ARTICLE: {
      GET: (param: string) => `/api/articles/get${param}`,
      GET_BY_ID: (id: string) => `/api/articles/get/${id}`,
      POST: '/api/articles/post',
      PUT: (id: string) => `/api/articles/put/${id}`,
      ADMIN_GET: (param: string) => `/api/articles/admin${param}`,
    },
  },
  EXPLORE_ALL_PODCAST: '/wedding-podcast',
}

interface LocationSingle {
  abbreviation: string
  country_id: number
  created_at: string
  id: number
  image_id: number
  name: string
  region_id: number
  updated_at: string
}

type WeddingType = {
  YEAR_SEASON: string
  YEAR_MONTH: string
  DATE: string
}

export const WEDDING_TYPE: WeddingType = {
  YEAR_SEASON: 'year_season',
  YEAR_MONTH: 'year_month',
  DATE: 'date',
}

export const weddingType: { [key: number]: string } = {
  1: WEDDING_TYPE.YEAR_SEASON,
  2: WEDDING_TYPE.YEAR_MONTH,
  3: WEDDING_TYPE.DATE,
}

export const statusMapping = {
  CREATED: 'CREATED',
  PENDING: 'PENDING',
  ACTIVE: 'ACTIVE',
  DECLINED: 'DECLINED',
} as const

export const SubmitForReviewLabel: string = 'Submit for review'

export const BlockTypeMaping: any = {
  1: 'content',
  2: 'image_grid',
  3: 'quote',
  5: 'column',
  6: 'featured_suppliers',
  7: 'cta',
  8: 'html',
  9: 'single_image',
  10: 'content',
  11: 'content',
  12: 'button',
  13: 'embedded_video',
  14: 'embedded_instagram',
}

const CURRENCY_UNIT = {
  GBP: '£',
  USD: '$',
  THB: '฿',
}

export const OptionsSeasion = [
  {
    value: 'summer',
    label: 'Summer',
  },
  {
    value: 'autumn',
    label: 'Autumn',
  },
  {
    value: 'winter',
    label: 'Winter',
  },
  {
    value: 'spring',
    label: 'Spring',
  },
]

export const OptionsMonth = [
  {
    value: 1,
    label: 'January',
  },
  {
    value: 2,
    label: 'February',
  },
  {
    value: 3,
    label: 'March',
  },
  {
    value: 4,
    label: 'April',
  },
  {
    value: 5,
    label: 'May',
  },
  {
    value: 6,
    label: 'June',
  },
  {
    value: 7,
    label: 'July',
  },
  {
    value: 8,
    label: 'August',
  },
  {
    value: 9,
    label: 'September',
  },
  {
    value: 10,
    label: 'October',
  },
  {
    value: 11,
    label: 'November',
  },
  {
    value: 12,
    label: 'December',
  },
]

export const OptionsDateWedding = [
  {
    value: 0,
    label: 'I know my wedding...',
    type: '',
  },
  {
    value: 1,
    label: 'Season / year',
    type: 'year_season',
  },
  {
    value: 2,
    label: 'Month / year',
    type: 'year_month',
  },
  {
    value: 3,
    label: 'Date',
    type: 'date',
  },
]

export const SupplierListingMenu = [
  { name: 'Listing Details', value: 'listing-details' },
  { name: 'Features & services', value: 'features-and-services' },
  { name: 'Gallery', value: 'gallery' },
  { name: 'Social Profiles', value: 'social-profiles' },
  { name: 'Reviews', value: 'testimonials' },
  { name: 'Offers & promotions', value: 'offers-and-promotions' },
  { name: 'FAQs', value: 'faqs' },
  { name: 'Events', value: 'events' },
] as const

export type SupplierListingMenuName =
  (typeof SupplierListingMenu)[number]['name']

export const formErrorMessage: { [key: string]: string } = {
  listing_name: 'Listing name field is required.',
  listing_name_exists: 'Listing name already exists.',
  listing_summary: 'The listing summary field is required.',
  business_description: 'The business description field is required.',
  gallery_list: 'The photos are required at least 8 photos.',
  feature_images: 'The Header image is required.',
  business_category: 'The Suppler/Business category field is required',
}

const AccountTab = [
  { name: 'Details', value: 'account', path: '?tab=account' },
  {
    name: 'Membership & Billing',
    value: 'membership',
    path: '?tab=membership',
  },
]

export const MESSAGE_TAB = [
  { name: 'All Messages', value: 'all-message', path: '?tab=all-message' },
  {
    name: 'Message Thread ',
    value: 'message-thread',
    path: '?tab=message-thread',
  },
]

export const SupplierDashboard = [
  { name: 'My Dashboard', value: 'summary' },
  { name: 'My Messages', value: 'messages', queryString: MESSAGE_TAB },
  {
    name: 'My Account',
    value: 'account',
    queryString: AccountTab,
  },
  { name: 'My Listings', value: 'listings' },
  { name: 'Submit Real Wedding', value: 'submit-real-wedding' },
]

export const Components: any = {
  content: dynamic(
    () =>
      import('@/components/v3/unique/article/blocks/content/content').then(
        (mod) => mod.ContentV3
      ),
    {
      ssr: true,
    }
  ),
  // image_grid: dynamic(
  //   () => import('@/components/v3/unique/article/blocks/grid-image/grid-image'),
  //   {
  //     ssr: true,
  //   }
  // ),
  // quote: dynamic(
  //   () => import('@/components/v3/unique/article/blocks/quote/quote'),
  //   { ssr: true }
  // ),
  // cta: dynamic(() => import('@/components/v3/unique/article/blocks/cta/cta'), {
  //   ssr: true,
  // }),
  column: dynamic(
    () =>
      import('@/components/v3/unique/article/blocks/columns/columns').then(
        (mod) => mod.ColumnsV3
      ),
    {
      ssr: true,
    }
  ),
}

export const ACTION_VIEW = {
  DESKTOP: 'desktop',
  TABLET: 'tablat',
  MOBILE: 'mobile',
}

export const OnboardingPage = [
  { name: 'Onboarding board one', value: 'event' },
  { name: 'Onboarding board two', value: 'tag' },
  { name: 'Onboarding board two', value: 'category' },
]

export const DATE_FORMAT = {
  DD_MMM_YYYY: 'DD MMM YYYY',
  DD_MM_YYYY: 'DD/MM/YYYY',
  YYYY_MM_DD: 'YYYY/MM/DD',
  YYYY_MM_DD_2: 'YYYY-MM-DD',
}
export const VENUE_CATEGORY_ID = 18
export const INTERNATIONAL_COUNTRY_ID = 5

export const MESSAGE_TYPE = {
  BROCHURE: 'brochure',
  ENQUIRY: 'enquiry',
}

export const FAVOURITE_TAB = [
  { name: 'All', value: 'all', path: '?tab=all' },
  {
    name: 'Inspiration & Planning',
    value: 'article',
    path: '?tab=article',
  },
  {
    name: 'Venues',
    value: 'venue',
    path: '?tab=venue',
  },
  {
    name: 'Suppliers',
    value: 'supplier',
    path: '?tab=supplier',
  },
  // Shown only while the `affiliateShop` feature flag is on — the favourites
  // page filters this entry out otherwise.
  {
    name: 'Products',
    value: 'product',
    path: '?tab=product',
  },
]

export const CUSTOMER_ACCOUNT_TAB = [
  { name: 'Details', value: 'details', path: '?tab=details' },
]

export const CustomerDashboard = [
  { name: 'Dashboard', value: 'summary' },
  // temporary hide my suppliers and checklist by removing it's names
  { name: '', value: 'my-suppliers' },
  {
    name: '',
    value: 'checklist',
  },
  { name: 'Favourites', value: 'favourites', queryString: FAVOURITE_TAB },
  { name: 'Messages', value: 'messages', queryString: MESSAGE_TAB },
  {
    name: 'my account',
    value: 'my-account',
    queryString: CUSTOMER_ACCOUNT_TAB,
  },
]

export const CHAT_TYPE = {
  ENQUIRY: 'enquiry',
  BROCHURE: 'brochure',
  COLLABORATION_PENDING: 'collaboration_pending',
  COLLABORATION_ACCEPTED: 'collaboration_accepted',
  COLLABORATION_DECLINED: 'collaboration_declined',
}

export const internationalID = 5

export const Season = {
  WINTER: 'winter',
  SPRING: 'spring',
  SUMMER: 'summer',
  AUTUMN: 'autumn',
}

export const CHECKLIST_BUTTON_TYPE = {
  ADD_TASK: 'add_task',
  FILTERS: 'filters',
  CLEAR: 'clear',
  APPLY: 'apply',
}

export const CUSTOMER_CHECKLIST_FILTERS = [
  { label: '12+ months', value: 360 },
  { label: '9 months', value: 270 },
  { label: '6 months', value: 180 },
  { label: '4 months', value: 120 },
  { label: '2 months', value: 60 },
  { label: '1 month', value: 30 },
  { label: '1 week', value: 7 },
]

export enum FAVOURITE {
  ARTICLE = 'ARTICLE',
  SUPPLIER = 'SUPPLIER',
  RECYCLE = 'RECYCLE',
  VENUE = 'VENUE',
  PRODUCT = 'PRODUCT',
}

export const CHECKLIST_MODAL_BUTTON_TYPE = {
  ADD_TASK: 'add_task',
  CANCEL: 'cancel',
  DELETE: 'delete',
}

export const SORT_BY = {
  BY_CREATED_DESC: 'BY_CREATED_DESC',
  BY_LATEST_UPDATED: 'BY_LATEST_UPDATED',
  BY_LOW_PRICE_TO_HIGH: 'BY_LOW_PRICE_TO_HIGH',
  BY_HIGH_PRICE_TO_LOW: 'BY_HIGH_PRICE_TO_LOW',
}

export type RecycleSortByKeys = keyof typeof SORT_BY

export const SUPPLIER_FILTERS = {
  CREATED: 'CREATED',
  ACTIVE: 'ACTIVE',
  PENDING: 'PENDING',
  PAID: 'PAID',
  DECLINED: 'DECLINED',
  INACTIVE: 'INACTIVE',
}

export * from './stripe'
export { widgetIndex, widgetsData } from './widget'

export const IS_PRODUCTION =
  process.env.NEXT_PUBLIC_APP_ENV?.toLowerCase() === 'prod'

const STRIPE_DASHBOARD_URL =
  process.env.NEXT_PUBLIC_STRIPE_DASHBOARD_URL || 'https://dashboard.stripe.com'

export const STRIPE_LINK_CUSTOMER = `${STRIPE_DASHBOARD_URL}${
  IS_PRODUCTION ? '/customers/' : '/test/customers/'
}`

export const STRIPE_LINK_SUBSCRIPTION = `${STRIPE_DASHBOARD_URL}${
  process.env.NEXT_PUBLIC_APP_ENV === 'PROD'
    ? '/subscriptions/'
    : '/test/subscriptions/'
}`

const ROUTE_CUSTOMER_ROOT = '/customer'
const ROUTE_PREVIEW_ROOT = '/preview'
const ROUTE_SUPPLIER = '/supplier'
export const ROUTES = {
  ABOUT_US: '/about-us',
  AUTH: {
    SIGN_UP: '/sign-up',
    SIGN_IN: '/sign-in',
    TWO_FA_SETUP: '/sign-in/2fa-setup',
    TWO_FA_VERIFY: '/sign-in/2fa-verify',
    FORGOTTEN_PASSWORD: '/forgotten-password',
    VERIFY_ACCOUNT: '/verify-account',
  },
  ADMIN: {
    ROOT: '/admin',
    NEW_FEATURES: '/admin/new-features',
    CATEGORIES: '/admin/categories',
    SUBCATEGORIES: '/admin/subcategories',
    SUPPLIER_AND_VENUE_CARDS: '/admin/supplier-and-venue-cards',
    HOMEPAGE_HERO_SECTION: '/admin/homepage-hero-section',
    HOMEPAGE_SUPPLIER_AND_VENUE_CARDS:
      '/admin/homepage-supplier-and-venue-cards',
    AUTHOR_PAGES: '/admin/author-pages',
    SUPPLIER_CAROUSEL_COPY: '/admin/supplier-carousel-copy',
    SOCIAL_MEDIA_MENTIONS: '/admin/social-media-mentions',
    EDITOR_CONSOLE: {
      ROOT: '/admin/editor-console',
      COMPOSER: '/admin/editor-console/composer',
      // App Router rebuild of the content editor. Deliberately a separate
      // segment from the Pages Router `/content` route so both can coexist.
      CONTENT_EDITOR: (articleId: number | string) =>
        `/admin/editor-console/content-editor/${articleId}`,
    },
    BUSINESS: {
      SUPPLIER_SEARCH: '/admin/business/supplier-search',
      LISTING_SEARCH: '/admin/business/listing-search',
      // A supplier account, keyed by its `supplier_application` id.
      ACCOUNT: (applicationId: number | string) =>
        `/admin/business/accounts/${applicationId}`,
      // A listing, keyed by its `supplier` id.
      LISTING: (listingId: number | string) =>
        `/admin/business/listings/${listingId}`,
    },
    SUPPLIER_CONSOLE: {
      ROOT: '/admin/supplier-console',
      SUPPLIER_ACCOUNT: '/admin/supplier-console/supplier-account/account',
      MANAGE_SUPPLIERS_PAGE: '/manage-suppliers-page',
      SUPPLIER_STATS: '/supplier-stats',
      MANAGE_CATEGORIES_DATA: '/manage-categories-data',
    },
    SETTINGS: {
      ROOT: '/admin/settings',
      SECURITY: '/admin/settings/security',
    },
    SHOP: {
      ROOT: '/admin/shop',
      LANDING: '/admin/shop/landing',
      MEGA_MENU: '/admin/shop/mega-menu',
      PRODUCTS: '/admin/shop/products',
      COLLECTIONS: '/admin/shop/collections',
      BRANDS: '/admin/shop/brands',
      RETAILERS: '/admin/shop/retailers',
    },
  },
  AUTHOR: '/author',
  ARTICLE: {
    ROOT: '/article',
  },
  SHOP: {
    ROOT: '/shop',
    COLLECTION: (slug: string) => `/shop/collection/${slug}`,
    BRAND: (slug: string) => `/shop/brands/${slug}`,
  },
  CUSTOMER: {
    CHECKLIST: ROUTE_CUSTOMER_ROOT + '/dashboard/checklist',
    DASHBOARD: ROUTE_CUSTOMER_ROOT + '/dashboard/summary',
    FAVOURITES: ROUTE_CUSTOMER_ROOT + '/dashboard/favourites',
    MESSAGES: ROUTE_CUSTOMER_ROOT + '/dashboard/messages',
    MY_SUPPLIERS: ROUTE_CUSTOMER_ROOT + '/dashboard/my-suppliers',
    ROOT: ROUTE_CUSTOMER_ROOT,
  },
  HOME: '/',
  INTERNAL_SERVER_ERROR: '/500',
  PODCAST: {
    ROOT: '/wedding-podcast',
  },
  PREVIEW: {
    ARTICLE: ROUTE_PREVIEW_ROOT + '/article',
    ROOT: ROUTE_PREVIEW_ROOT,
    WEDDING_SUPPLIERS: {
      ROOT: ROUTE_PREVIEW_ROOT + '/wedding-suppliers',
    },
    VENUE: {
      ROOT: ROUTE_PREVIEW_ROOT + '/wedding-venues',
    },
  },
  PRIVACY: '/privacy',
  READER: {
    DASHBOARD: '/customer/dashboard/summary',
    ONBOARDING: '/onboarding',
    MY_ACCOUNT: '/customer/dashboard/my-account',
  },
  SEARCH: '/search',
  SUPPLIER: {
    LISTING: ROUTE_SUPPLIER + '/listing',
    ROOT: ROUTE_SUPPLIER,
    SUMMARY: ROUTE_SUPPLIER + '/dashboard/summary',
    BILLING: ROUTE_SUPPLIER + '/dashboard/account?tab=membership',
    DASHBOARD_ROOT: ROUTE_SUPPLIER + '/dashboard',
    DASHBOARD: {
      SUMMARY: ROUTE_SUPPLIER + '/dashboard/summary',
      ACCOUNT: ROUTE_SUPPLIER + '/dashboard/account',
      SUBMIT_REAL_WEDDING: ROUTE_SUPPLIER + '/dashboard/submit-real-wedding',
      LISTINGS: ROUTE_SUPPLIER + '/dashboard/listings',
      MEMBERSHIP: ROUTE_SUPPLIER + '/dashboard/account?tab=membership',
      MEMBERSHIP_UPDATE: ROUTE_SUPPLIER + '/dashboard/account/membership',
      MESSAGES: ROUTE_SUPPLIER + '/dashboard/messages',
    },
    MEMBERSHIP_UPDATE_SUCCESS: ROUTE_SUPPLIER + '/membership/success',
    MEMBERSHIP_UPDATE_FAILED: ROUTE_SUPPLIER + '/membership/failed',
    PUBLISH_LISTING: ROUTE_SUPPLIER + '/publish-listing',
    THANK_YOU: ROUTE_SUPPLIER + '/thankyou',
    PRICING: '/supplier-pricing',
  },
  TERMS: '/terms',
  VENUE: {
    ROOT: '/wedding-venues',
    PRICING: '/venue-pricing',
  },
  WEDDING_INSPIRATION: '/wedding-inspiration',
  WEDDING_PLANNING: '/wedding-planning',
  WEDDING_SUPPLIERS: {
    APPLY: {
      FORM: '/supplier-application?view=form',
      ROOT: '/supplier-application',
    },
    ROOT: '/wedding-suppliers',
  },
  MESSAGES: {
    ALL_MESSAGES: '/customer/dashboard/messages?tab=all-message',
    THREAD: '/customer/dashboard/messages?tab=message-thread',
  },
}

export const PROD_URL = 'https://www.rockmywedding.co.uk'

export const API_V1 = `${process.env.NEXT_PUBLIC_BASE_URL}/api/v1`
export function getApiV1BaseUrl() {
  return process.env.NEXT_PUBLIC_LOCAL_API_BASE
    ? `${process.env.NEXT_PUBLIC_LOCAL_API_BASE}/api/v1`
    : API_V1
}

export const API_V2 = `${process.env.NEXT_PUBLIC_BASE_URL}/api/v2`
export function getApiV2BaseUrl() {
  return process.env.NEXT_PUBLIC_LOCAL_API_BASE
    ? `${process.env.NEXT_PUBLIC_LOCAL_API_BASE}/api/v2`
    : API_V2
}

export const FE_URL = process.env.NEXT_PUBLIC_BASE_URL
export const LOGO_FILE_NAME = 'rock-my-wedding-logo'
export const RMW_LOGO_URL = `${PROD_URL}/${LOGO_FILE_NAME}.png`

const API_ADMIN_ROOT = '/admin'
const API_ARTICLE_ROOT = '/articles'
const API_AUTH_ROOT = '/auth'
const API_CHATROOMS_ROOT = '/chatrooms'
const API_CUSTOMER_EVENTS_ROOT = '/customer-events'
const API_IMAGES_ROOT = '/images'
const API_SUPPLIER_ROOT = '/supplier'
const API_SUPPLIERS_ROOT = '/suppliers'
const API_USERS_ROOT = '/users'

export const API_URL = {
  ADMIN: {
    ROOT: API_ADMIN_ROOT,
    REVIEWERS: API_ADMIN_ROOT + '/reviewers',
    SUPPLIER_APPLICATION: API_ADMIN_ROOT + '/suppliers/application',
    SUPPLIER: '/admin/supplier',
    SUPPLIERS_REPORT: API_ADMIN_ROOT + API_SUPPLIERS_ROOT + '/report',
    SUPPLIERS_ARTICLES_REPORT:
      API_ADMIN_ROOT + API_SUPPLIERS_ROOT + '/report' + '/articles',
    IMAGES: API_ADMIN_ROOT + '/images',
    SUPPLIER_INVITE: API_ADMIN_ROOT + '/supplier/invite',
    CREATE_SUPPLIER_LINK: API_ADMIN_ROOT + '/suppliers/create-account-link',
    IMAGE: API_ADMIN_ROOT + '/images',
    SUPPLIERS_STATS: API_ADMIN_ROOT + '/suppliers/stats',
    SUPPLIERS: {
      LISTINGS: API_ADMIN_ROOT + '/suppliers/listings',
      STRIPE: (id: number | string) =>
        API_ADMIN_ROOT + `/suppliers/${id}/stripe`,
    },
    USERS: {
      ROOT: API_ADMIN_ROOT + '/users',
      CUSTOMER_EVENTS: API_ADMIN_ROOT + '/users/customer-events',
      STRIPE_CUSTOMER: (id: number | string) =>
        API_ADMIN_ROOT + `/users/${id}/stripe-customer`,
    },
  },
  ARTICLES: {
    ROOT: API_ARTICLE_ROOT,
    PATH: API_ARTICLE_ROOT + '/path',
    CATEGORIES: API_ARTICLE_ROOT + '/categories',
    SUBCATEGORIES: API_ARTICLE_ROOT + '/subcategories',
    GET_BY_ID: API_ARTICLE_ROOT + `/id`,
    PODCAST: API_ARTICLE_ROOT + '/podcast',
    RECOMMENDED: API_ARTICLE_ROOT + '/recommended',
    SEARCH: API_ARTICLE_ROOT + '/search',
  },
  AUTH: {
    LOGIN: API_AUTH_ROOT + '/login',
    LOGOUT: API_AUTH_ROOT + '/logout',
    REGISTER: API_AUTH_ROOT + '/register',
    FACEBOOK: API_AUTH_ROOT + '/facebook',
    GOOGLE: API_AUTH_ROOT + '/google',
    COMPLETE_LOGIN: API_AUTH_ROOT + '/complete-login',
  },
  BUSINESS_CATEGORIES: '/business-categories',
  CUSTOMER_EVENTS: {
    ROOT: API_CUSTOMER_EVENTS_ROOT,
    NUMBER_OF_GUESTS: API_CUSTOMER_EVENTS_ROOT + '/number-of-guests',
    IDEAL_BUDGET: API_CUSTOMER_EVENTS_ROOT + '/ideal-budget',
  },
  FAVOURITES: {
    ROOT: '/favourite',
  },
  LOCATIONS: {
    ROOT: '/locations', // @deprecated, use /regions
  },
  REGIONS: '/regions',
  SUPPLIERS: {
    ROOT: API_SUPPLIERS_ROOT,
    PAGINATION_SEARCH: API_SUPPLIERS_ROOT + '/pagination-search',
    SEARCH: API_SUPPLIERS_ROOT + '/search',
    GET_LISTING_BY_SLUG: API_SUPPLIERS_ROOT + '/article',
    SUGGESTED: API_SUPPLIERS_ROOT + '/suggested',
    SHUFFLED: API_SUPPLIERS_ROOT + '/shuffled',
  },
  SUPPLIER: {
    ROOT: API_SUPPLIER_ROOT,
    INTERACTION: API_SUPPLIER_ROOT + '/interaction',
    PRICE: API_SUPPLIER_ROOT + '/price',
    ARTICLE: API_SUPPLIER_ROOT + '/article',
    APPLY: API_SUPPLIER_ROOT + '/apply',
  },
  TAG: {
    ROOT: '/tags',
  },
  UI_PLACEMENT: {
    ROOT: '/ui-placement',
  },
  USERS: {
    ROOT: API_USERS_ROOT,
    CURRENT_USER: API_USERS_ROOT + '/current-user',
    VERIFY_EMAIL: API_USERS_ROOT + '/verify-email',
    CHECK_EMAIL_EXIST: API_USERS_ROOT + '/check-email-exist',
  },
  REDIRECTS: {
    ROOT: '/redirect',
  },
  CHATROOMS: {
    ROOT: API_CHATROOMS_ROOT,
  },
  METADATA: {
    SITEMAPS: '/metadata/sitemap',
    DYNAMIC_SITEMAPS: '/metadata/sitemaps/dynamic',
    SITEMAP_ARTICLE_CATEGORY: '/metadata/sitemaps/article-category',
    SITEMAP_INDEX: '/metadata/sitemap_index',
  },
}

export const SEND_MAIL_API_SECRET_HEADER = 'x-send-mail-secret'
export const SEND_MAIL_API_SECRET = process.env.SEND_MAIL_API_SECRET!

export const NEXT_API_URL = {
  SUPPLIER: {
    INTERACTION: '/api' + API_SUPPLIER_ROOT + '/interaction',
  },
  ADMIN: {
    ARTICLES: '/api' + API_ARTICLE_ROOT + '/admin',
    IMAGES: '/api' + '/admin' + API_IMAGES_ROOT,
  },
  SHOP: {
    MEGA_MENU: '/shop/mega-menu',
  },
  GET_TAGS: '/api/getTags',
  GET_IMAGE_LIBRARY: '/api/getImageLibrary',
  REVALIDATE: '/revalidate',
  SEND_MAIL: '/send-mail',
  LISTING_PATH: (listingId: number) => `/listings/${listingId}/path`,
}

export const BLOCK_AREA = {
  MAIN: 'Main',
  OTHER_SUPPLIERS: 'Other suppliers',
}

export const ONE_MINUTE_IN_SECONDS = 60
export const ONE_HOUR_IN_SECONDS = 60 * 60
export const ONE_SECOND_IN_MILLISECONDS = 1000

export const GTM_TRACKING_IDS = {
  SUPPLIER_CLICK_THROUGHS: 'gtm-supplier-website-link',
  SUPPLIER_SHARE_CLICK: 'gtm-supplier-share-click',
}

export const GTM_EVENT = {
  ENQUIRY_SUBMIT: 'enquiry_submit',
  SUPPLIER_PROFILE_CLICK: 'supplier_profile_click',
  SUPPLIER_WEBSITE_LINK_CLICK: 'supplier-website-link',
  SIGN_UP: 'sign_up',
  ARTICLE_VIEW: 'article_view',
  SUPPLIER_PROFILE_VIEW: 'supplier_profile_view',
  NAV_CLICK: 'nav_click',
  CATEGORY_PAGE_VIEW: 'category_page_view',
  SUPPLIERS_CTA_SEARCH_CLICK: 'suppliers_search_cta_click',
  SOCIAL_CLICK_SUPPLIER_PROFILE: 'Social_click_supplier_profile',
  SUPPLIER_PHONE_SHOWN: 'Supplier_phone_click',
  SUPPLIER_CARD_INSTAGRAM_CLICK: 'supplier_card_instagram_click',
  SUPPLIER_CARD_WEBSITE_CLICK: 'supplier_card_website_click',
  SHOP_PRODUCT_CLICK: 'shop_product_click',
  CATEGORY_IMPRESSION: 'category_impression',
  CAROUSEL_IMPRESSION: 'carousel_impression',
}

export const LOCAL_STORAGE_KEYS = {
  GTM_EVENT: {
    PROVIDER_SIGN_UP_URL: 'ProviderAuthSignUpUrl',
  },
}

export const SLIDER_CLASS_NAME = 'keen-slider__slide'

// Articles shown in the "Read more" slider at the foot of a shop collection
// page. Also the number the admin panel checks a chosen category against before
// warning that it can't fill the slider.
export const SHOP_COLLECTION_ARTICLES_COUNT = 9

// Number of articles shown in an article's "Read More" section when no
// related articles have been manually curated for it.
export const RELATED_ARTICLES_FALLBACK_COUNT = 9

export const SOCIAL_LINKS = {
  FACEBOOK: 'https://www.facebook.com/rockmywedding',
  INSTAGRAM: 'https://www.instagram.com/rockmywedding/',
  PINTEREST: 'https://www.pinterest.co.uk/rockmywedding/',
  TWITTER: 'https://twitter.com/RockMyWedding',
  TIKTOK: 'https://www.tiktok.com/@rockmywedding?lang=en',
}

export const MEMBERSHIP_SUPPORT_MAIL = 'membership@rockmywedding.co.uk'
export const HELLO_MAIL = 'hello@rockmywedding.co.uk'

export const FAVICON_URL = `${PROD_URL}/assets/icons/favicon/favicon.png`

export const SUBCATEGORY_PAGE_DATA = {
  ARTICLES_PER_PAGE: 21,
  ARTICLES_PER_PARAGRAPH: 7,
}

export const LAUNCH_DATE = '2024-08-08'

export const CATEGORY_PAGE = {
  viewAllArticlesName: 'All',
}

// hide temporary subcategories with less than 11 articles assigned
export const HIDDEN_SUBCATEGORIES = [
  'budget',
  'celebrity-weddings',
  'engagement-parties',
  'engagement-rings',
  'nails',
  'proposals',
  'proposals-abroad',
  'reception',
  'speeches',
  'stationery',
  'wedding-rings',
]

export const HIDDEN_CATEGORIES = ['/stag']

export const BLOCK_TYPE = {
  CONTENT: 1,
  IMAGE_GRID: 2,
  QUOTE: 3,
  COLUMNS: 5,
  REFERENCE: 6,
  CTA: 7,
  LEGACY_CONTENT: 8, // to removed after BLOCK_TYPE 8 will be migrated to EMBEDDED_VIDEO block
  SINGLE_IMAGE: 9,
  BUTTON: 12,
  EMBEDDED_VIDEO: 13,
  EMBEDDED_INSTAGRAM: 14,
  // Featured supplier cards slider. Not to be confused with REFERENCE (6),
  // which is the plain-text supplier link list.
  SUPPLIER_CARDS: 15,
  // The recommended supplier credit banner, always directly under the article
  // hero. It lists the SUPPLIER_CARDS (15) selection; the block itself only
  // marks that the article carries the banner.
  SUPPLIER_CREDITS: 16,
  // In-article Topical/Discovery supplier carousel. Every article shows one;
  // the block carries only the editor's overrides and pinned position.
  SUPPLIER_CAROUSEL: 17,
}

export const RECAPTCHA_ACTIONS = {
  ENQUIRY_FORM: 'enquiry_form',
  SIGN_UP: 'sign_up',
}

export const SUPPLIER_SECTION_ID = {
  LOCATIONS: 'locations',
  ENQUIRY_NOW: 'enquiry-now',
  REVIEWS: 'reviews',
  CAPACITY: 'capacity',
  VENUE_HIRE: 'venue-hire',
  SUPPLIER_COST: 'supplier-cost',
}

export const STRIPE_COUPONS = {
  SUPPLIER_YEARLY_COUPON: process.env.NEXT_PUBLIC_STRIPE_SUPPLIER_YEARLY_COUPON,
  VENUE_YEARLY_COUPON: process.env.NEXT_PUBLIC_STRIPE_VENUE_YEARLY_COUPON,
}

export enum STRIPE_SUBSCRIPTION_INTERVAL {
  YEARLY = 'YEARLY',
}

export const STRIPE_PRICE_MAPPING = {
  SUPPLIER: {
    BRONZE: {
      YEARLY: process.env.NEXT_PUBLIC_STRIPE_SUPPLIER_BRONZE_YEARLY,
      MONTHLY: process.env.NEXT_PUBLIC_STRIPE_SUPPLIER_BRONZE_MONTHLY,
    },
    SILVER: {
      YEARLY: process.env.NEXT_PUBLIC_STRIPE_SUPPLIER_SILVER_YEARLY,
      MONTHLY: process.env.NEXT_PUBLIC_STRIPE_SUPPLIER_SILVER_MONTHLY,
    },
    GOLD: {
      YEARLY: process.env.NEXT_PUBLIC_STRIPE_SUPPLIER_GOLD_YEARLY,
      MONTHLY: process.env.NEXT_PUBLIC_STRIPE_SUPPLIER_GOLD_MONTHLY,
    },
  },
  VENUE: {
    BRONZE: {
      YEARLY: process.env.NEXT_PUBLIC_STRIPE_VENUE_BRONZE_YEARLY,
      MONTHLY: process.env.NEXT_PUBLIC_STRIPE_VENUE_BRONZE_MONTHLY,
    },
    SILVER: {
      YEARLY: process.env.NEXT_PUBLIC_STRIPE_VENUE_SILVER_YEARLY,
      MONTHLY: process.env.NEXT_PUBLIC_STRIPE_VENUE_SILVER_MONTHLY,
    },
    GOLD: {
      YEARLY: process.env.NEXT_PUBLIC_STRIPE_VENUE_GOLD_YEARLY,
      MONTHLY: process.env.NEXT_PUBLIC_STRIPE_VENUE_GOLD_MONTHLY,
    },
  },
}

export const REQUIRED_3DS_ACTION_COPY = `Your Bronze subscription payment was unsuccessful. Click <b>’Authorise payment’</b> to complete the authorisation with your bank.`

export const CACHE_TAGS = {
  SUPPLIER_LISTING_SEO_TEXT: 'supplier_listing_seo_text',
}

export const KNOW_US_FROM = [
  { value: 'Social media', label: 'Social media' },
  { value: 'Family member or friend', label: 'Family member or friend' },
  {
    value: 'From a recommended supplier',
    label: 'From a Recommended Supplier',
  },
  { value: 'Other', label: 'Other' },
]

export const CHECKLIST_TEMPLATE = [
  {
    timeBefore: 12,
    timeBeforeDays: 360,
    planners: [
      {
        id: 2,
        title: 'Work out guest list and choose bridal party',
        notes: '',
        complete: false,
      },
      { id: 1, title: 'Work out your budget', notes: '', complete: false },
      {
        id: 10,
        title: 'Research honeymoon options',
        notes: '',
        complete: false,
      },
      {
        id: 9,
        title: 'Consider underwear and try on dresses',
        notes: '',
        complete: false,
      },
      {
        id: 8,
        title: 'Book your photographer and videographer',
        notes: '',
        complete: false,
      },
      {
        id: 7,
        title: 'Book a wedding planner if you want one and budget allows',
        notes: '',
        complete: false,
      },
      {
        id: 6,
        title: 'Arrange appointments at wedding dress boutiques',
        notes: '',
        complete: false,
      },
      { id: 5, title: 'Sort wedding insurance', notes: '', complete: false },
      {
        id: 4,
        title: 'Book officiant / church etc. and sort wedding licence',
        notes: '',
        complete: false,
      },
      {
        id: 3,
        title: 'Shortlist venues and check availability',
        notes: '',
        complete: false,
      },
    ],
  },
  {
    timeBefore: 9,
    timeBeforeDays: 270,
    planners: [
      { id: 11, title: 'Book florist', notes: '', complete: false },
      { id: 12, title: 'Send Save The Dates', notes: '', complete: false },
      { id: 13, title: 'Look into Grooms attire', notes: '', complete: false },
    ],
  },
  {
    timeBefore: 6,
    timeBeforeDays: 180,
    planners: [
      {
        id: 22,
        title: 'Book hair and makeup trials',
        notes: '',
        complete: false,
      },
      {
        id: 21,
        title: 'Decide on a gift list company and register',
        notes: 'The Wedding Shop,\n\nPrezola,\n\nThe Wedding Present Company',
        complete: false,
      },
      {
        id: 20,
        title: 'Research and book any items you may need to hire',
        notes: '',
        complete: false,
      },
      {
        id: 19,
        title: 'Consider transport',
        notes: 'For both you, your wedding party and if need be, your guests',
        complete: false,
      },
      {
        id: 17,
        title: 'Book entertainment. Musical and other',
        notes: '',
        complete: false,
      },
      {
        id: 16,
        title: 'Taste cakes and book your cake',
        notes: '',
        complete: false,
      },
      { id: 15, title: 'Confirm catering', notes: '', complete: false },
      {
        id: 14,
        title: 'Shop for bridesmaid dresses',
        notes: '',
        complete: false,
      },
      {
        id: 18,
        title: 'Book stationery',
        notes: 'Either book a professional or plan your DIY stationery',
        complete: false,
      },
    ],
  },
  {
    timeBefore: 4,
    timeBeforeDays: 120,
    planners: [
      {
        id: 23,
        title:
          "Buy your underwear (if you didn't get it before trying dresses)",
        notes: '',
        complete: false,
      },
      { id: 24, title: 'Shop for shoes', notes: '', complete: false },
      {
        id: 25,
        title:
          'Buy or hire Grooms / Groomsmen attire and leave time for alterations / fittings',
        notes: '',
        complete: false,
      },
      { id: 26, title: 'Send invitations', notes: '', complete: false },
      { id: 27, title: 'Choose wedding rings', notes: '', complete: false },
      {
        id: 28,
        title: 'Have hair and makeup trials and book your favourite',
        notes: '',
        complete: false,
      },
    ],
  },
  {
    timeBefore: 2,
    timeBeforeDays: 60,
    planners: [
      { id: 29, title: 'Organise dress fittings', notes: '', complete: false },
      { id: 34, title: 'Chase RSVPs', notes: '', complete: false },
      {
        id: 33,
        title: 'Have a pre-wedding shoot with your photographer',
        notes: '',
        complete: false,
      },
      {
        id: 32,
        title: 'Finalise order of service and the day',
        notes: '',
        complete: false,
      },
      {
        id: 31,
        title: "Finalise readings and choose who's reading them",
        notes: '',
        complete: false,
      },
      { id: 30, title: 'Choose music', notes: '', complete: false },
    ],
  },
  {
    timeBefore: 1,
    timeBeforeDays: 30,
    planners: [
      {
        id: 35,
        title: 'Arrange your seating plan',
        notes: '',
        complete: false,
      },
      {
        id: 36,
        title: 'Start making your table plan (if making it yourself)',
        notes: '',
        complete: false,
      },
      { id: 37, title: 'Write vows', notes: '', complete: false },
      {
        id: 38,
        title: 'Book beauty and spa treatments',
        notes: '',
        complete: false,
      },
    ],
  },
  {
    timeBefore: 0,
    timeBeforeDays: 7,
    planners: [
      { id: 39, title: 'Dry clean your suit', notes: '', complete: false },
    ],
  },
]

export const SLACK_EMAIL_SPAM_ALERT_WEBHOOK_URL =
  process.env.SLACK_EMAIL_SPAM_ALERT_WEBHOOK_URL
